import React from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { CountrySelect } from "@/components/ui/country-select";

type ContactFormProps = {
  form: UseFormReturn<any>;
};

export function ContactForm({ form }: ContactFormProps) {
  const contacts = form.watch("contacts") || [];

  const addContact = () => {
    const currentContacts = form.getValues("contacts") || [];
    form.setValue("contacts", [
      ...currentContacts,
      {
        contactName: "",
        contactEmail: "",
        countryCode: "+1",
        phoneNumber: "",
        contactRole: "",
      },
    ]);
  };

  const removeContact = (index: number) => {
    const currentContacts = form.getValues("contacts") || [];
    const updatedContacts = currentContacts.filter((_, i) => i !== index);
    form.setValue("contacts", updatedContacts);
  };

  return (
    <div className="space-y-6">
      {contacts.map((contact: any, index: number) => (
        <div key={index} className="space-y-4 p-4 bg-gray-50 rounded-md border">
          <div className="flex justify-between items-center">
            <Label className="text-lg font-medium">
              Contact Person {index + 1}
            </Label>
            {index > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeContact(index)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name={`contacts.${index}.contactName`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name*</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John Smith"
                      {...field}
                      onChange={(e) => {
                        // Only allow letters and spaces
                        const value = e.target.value.replace(
                          /[^a-zA-Z\s]/g,
                          ""
                        );
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`contacts.${index}.contactRole`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role*</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter role (e.g., Primary Contact)"
                      {...field}
                      onChange={(e) => {
                        // Only allow letters and spaces
                        const value = e.target.value.replace(
                          /[^a-zA-Z\s]/g,
                          ""
                        );
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name={`contacts.${index}.contactEmail`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email*</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <FormLabel>Phone Number*</FormLabel>
              <div className="flex gap-2">
                <FormField
                  control={form.control}
                  name={`contacts.${index}.countryCode`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <CountrySelect
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`contacts.${index}.phoneNumber`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          placeholder="1234567890"
                          {...field}
                          onChange={(e) => {
                            // Only allow positive numbers
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addContact}
        className="w-full border-dashed border-2 hover:bg-gray-50"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Another Contact
      </Button>
    </div>
  );
}
